import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { GetAirportMapGroupDto, GetAirportMissionZoneDto, GetMissionsFromMapDto, MissionType, MissionsClient } from '../api/api.g';
import { MapLocation } from '../models/MapModels';
import { MissionsHelper } from '../models/helpers/missionsHelper';
import { MissionCardProps } from '../components/common/mission/MissionCard';
import { FilterItemProps } from '../components/common/filter/FilterItem';
import { MapMode } from './useMapViewport';
import { SideBarState } from './useMapSidebarState';
import { isMissionAvailableForDiscovery } from '../models/helpers/missionAvailabilityHelper';

export interface UseMissionsDataParams {
  apiMissions: ReturnType<typeof MissionsClient extends new (...args: any) => any ? any : never> | any;
  apiAirportMissions: ReturnType<typeof MissionsClient extends new (...args: any) => any ? any : never> | any;
  authToken?: string;
  uiLanguage: string;
  effectiveLocation?: MapLocation;
  serviceLocation?: MapLocation;
  fallBackLocation: MapLocation;
  userLocation?: MapLocation;
  currentLocation?: MapLocation;
  locationReady: boolean;
  hasResolvedUserPosition: boolean;
  waitingForBrowserLocation: boolean;
  hasGpsFix: boolean;
  mapMode: MapMode;
  hasViewportBounds: boolean;
  appliedTopLeftLocation?: string;
  appliedBottomRightLocation?: string;
  missionQuerySeed?: string;
  hasCenteredOnEffectiveLocation: boolean;
  sideBarState: SideBarState;
  branchMarker?: string;
  selectedMissionCardForDetailsId?: string;
}

export interface UseMissionsDataResult {
  missions: GetMissionsFromMapDto[] | undefined;
  airportMapGroups: GetAirportMapGroupDto[];
  missionCardProps: MissionCardProps[];
  setMissionCardProps: React.Dispatch<React.SetStateAction<MissionCardProps[]>>;
  airportMissionCardProps: MissionCardProps[];
  setAirportMissionCardProps: React.Dispatch<React.SetStateAction<MissionCardProps[]>>;
  airportMapGroupsState: GetAirportMapGroupDto[];
  setAirportMapGroups: React.Dispatch<React.SetStateAction<GetAirportMapGroupDto[]>>;
  pinnedMissionCardProps: MissionCardProps[];
  setPinnedMissionCardProps: React.Dispatch<React.SetStateAction<MissionCardProps[]>>;
  flashMissionCardProps: MissionCardProps[];
  setFlashMissionCardProps: React.Dispatch<React.SetStateAction<MissionCardProps[]>>;
  initialMissionCardProps: MissionCardProps[];
  initialPinnedMissionCardProps: MissionCardProps[];
  initialFlashMissionCardProps: MissionCardProps[];
  initialAirportMissionCardProps: MissionCardProps[];
  filterItems: FilterItemProps[];
  setFilterItems: React.Dispatch<React.SetStateAction<FilterItemProps[]>>;
  selectedFilterItems: FilterItemProps[];
  setSelectedFilterItems: React.Dispatch<React.SetStateAction<FilterItemProps[]>>;
  onlyAcceptableMissions: boolean;
  setOnlyAcceptableMissions: React.Dispatch<React.SetStateAction<boolean>>;
  showUpcomingMissions: boolean;
  setShowUpcomingMissions: React.Dispatch<React.SetStateAction<boolean>>;
  canFetchMapMissions: boolean;
  canFetchAirportMissions: boolean;
  loadingSideContent: boolean;
  shouldShowMapMissionLoading: boolean;
  hasLoadedMissions: boolean;
  hasLoadedViewportMapResults: boolean;
  mapQuery: ReturnType<typeof useQuery<any, any>>;
  airportMapQuery: ReturnType<typeof useQuery<any, any>>;
  selectedBranchMissionDetailQuery: ReturnType<typeof useQuery<any, any>>;
  selectedMissionFallback: GetMissionsFromMapDto | undefined;
}

// Helper functions kept close to mission data so they can be reused
const normalizeTagValue = (value?: string) => {
  return (value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

const resolveMissionType = (mission: { missionType?: MissionType }) => {
  return mission.missionType ?? MissionType.Regular;
};

const resolveIsFlashMission = (mission: { isFlashMission?: boolean }) => {
  return !!mission.isFlashMission;
};

const AIRPORT_FALLBACK_BOUNDS_LAT_DELTA = 6;
const AIRPORT_FALLBACK_BOUNDS_LNG_DELTA = 10;
const GLOBAL_AIRPORT_TOP_LEFT_BOUNDS = '85.0000&-180.0000';
const GLOBAL_AIRPORT_BOTTOM_RIGHT_BOUNDS = '-85.0000&180.0000';

const clampCoordinate = (value: number, min: number, max: number) => {
  return Math.min(Math.max(value, min), max);
};

const buildWideAirportBounds = (latitude: number, longitude: number) => {
  const top = clampCoordinate(latitude + AIRPORT_FALLBACK_BOUNDS_LAT_DELTA, -85, 85);
  const bottom = clampCoordinate(latitude - AIRPORT_FALLBACK_BOUNDS_LAT_DELTA, -85, 85);
  const left = clampCoordinate(longitude - AIRPORT_FALLBACK_BOUNDS_LNG_DELTA, -180, 180);
  const right = clampCoordinate(longitude + AIRPORT_FALLBACK_BOUNDS_LNG_DELTA, -180, 180);

  return {
    topLeft: `${top.toFixed(4)}&${left.toFixed(4)}`,
    bottomRight: `${bottom.toFixed(4)}&${right.toFixed(4)}`,
  };
};

const parseBoundsCoordinate = (value?: string): MapLocation | undefined => {
  if (!value) {
    return undefined;
  }

  const [latRaw, lngRaw] = value.split('&');
  const lat = Number(latRaw);
  const lng = Number(lngRaw);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return undefined;
  }

  return { lat, lng };
};

const mapAirportGroupsToCards = (airportGroups: GetAirportMapGroupDto[]): MissionCardProps[] => {
  const normalizeRouteOption = (option: { zoneId?: string; name?: string }): GetAirportMissionZoneDto | undefined => {
    const zoneId = option.zoneId?.trim();
    if (!zoneId) {
      return undefined;
    }

    const zoneName = option.name?.trim();
    const zone = new GetAirportMissionZoneDto();
    zone.zoneId = zoneId;
    zone.name = zoneName && zoneName.length > 0 ? zoneName : zoneId;
    return zone;
  };

  const mapAirportGroupZonesFromMissions = (group: GetAirportMapGroupDto): GetAirportMissionZoneDto[] => {
    const zoneLookup = new Map<string, GetAirportMissionZoneDto>();
    const missions = [...(group.highOrderMissions ?? []), ...(group.missions ?? []), ...(group.lowOrderMissions ?? [])];

    missions.forEach((mission) => {
      const zoneId = mission.branchAirportInfo?.zoneId?.trim();
      if (!zoneId || zoneLookup.has(zoneId)) {
        return;
      }

      const zoneName = mission.branchAirportInfo?.zoneName?.trim();
      const zone = new GetAirportMissionZoneDto();
      zone.zoneId = zoneId;
      zone.name = zoneName && zoneName.length > 0 ? zoneName : zoneId;
      zoneLookup.set(zoneId, zone);
    });

    return Array.from(zoneLookup.values());
  };

  const mapAirportGroupRouteOptions = (group: GetAirportMapGroupDto): GetAirportMissionZoneDto[] => {
    if (Array.isArray(group.routeOptions)) {
      return group.routeOptions.reduce<GetAirportMissionZoneDto[]>((options, option) => {
        const zone = normalizeRouteOption(option);
        if (zone) {
          options.push(zone);
        }

        return options;
      }, []);
    }

    return mapAirportGroupZonesFromMissions(group);
  };

  return (airportGroups ?? [])
    .filter((group) => !!group.representativeBranchId && !!group.geocoordinate?.x && !!group.geocoordinate?.y)
    .map(
      (group): MissionCardProps => {
        const groupMissions = [...(group.highOrderMissions ?? []), ...(group.missions ?? []), ...(group.lowOrderMissions ?? [])];
        const hasAvailableMission = groupMissions.some((mission) => isMissionAvailableForDiscovery(mission));
        const hasAcceptableMission = groupMissions.some((mission) => !!mission.canAccept);

        return {
          id: group.representativeBranchId as string,
          geocoordinate: group.geocoordinate!,
          title: group.airportName ?? 'Airport',
          industry: 'airport-trip-popup-subtitle',
          distance: undefined,
          missionDetailCardProps: [],
          missionType: MissionType.Airport,
          airportMissionCount: group.missionCount ?? 0,
          airportRouteOptions: mapAirportGroupRouteOptions(group),
          canAccept: groupMissions.length > 0 ? hasAcceptableMission : undefined,
          isAvailableForDiscovery: groupMissions.length > 0 ? hasAvailableMission : undefined,
          onPress: () => undefined,
        };
      },
    );
};

export const useMissionsData = (params: UseMissionsDataParams): UseMissionsDataResult => {
  const {
    apiMissions,
    apiAirportMissions,
    authToken,
    uiLanguage,
    effectiveLocation,
    serviceLocation,
    fallBackLocation,
    userLocation,
    currentLocation,
    locationReady,
    hasResolvedUserPosition,
    waitingForBrowserLocation,
    hasGpsFix,
    mapMode,
    hasViewportBounds,
    appliedTopLeftLocation,
    appliedBottomRightLocation,
    missionQuerySeed,
    hasCenteredOnEffectiveLocation,
    sideBarState,
    branchMarker,
    selectedMissionCardForDetailsId,
  } = params;

  const [missions, setMissions] = useState<GetMissionsFromMapDto[]>();
  const [airportMapGroups, setAirportMapGroups] = useState<GetAirportMapGroupDto[]>([]);
  const [initialMissionCardProps, setInitialMissionCardProps] = useState<MissionCardProps[]>([]);
  const [initialAirportMissionCardProps, setInitialAirportMissionCardProps] = useState<MissionCardProps[]>([]);
  const [initialPinnedMissionCardProps, setInitialPinnedMissionCardProps] = useState<MissionCardProps[]>([]);
  const [initialFlashMissionCardProps, setInitialFlashMissionCardProps] = useState<MissionCardProps[]>([]);
  const [missionCardProps, setMissionCardProps] = useState<MissionCardProps[]>([]);
  const [airportMissionCardProps, setAirportMissionCardProps] = useState<MissionCardProps[]>([]);
  const [pinnedMissionCardProps, setPinnedMissionCardProps] = useState<MissionCardProps[]>([]);
  const [flashMissionCardProps, setFlashMissionCardProps] = useState<MissionCardProps[]>([]);
  const [filterItems, setFilterItems] = useState<FilterItemProps[]>([]);
  const [selectedFilterItems, setSelectedFilterItems] = useState<FilterItemProps[]>([]);
  const [onlyAcceptableMissions, setOnlyAcceptableMissions] = useState(false);
  const [showUpcomingMissions, setShowUpcomingMissions] = useState(true);
  const [loadingSideContent, setLoadingSideContent] = useState(false);
  const [lastAppliedMapDataUpdatedAt, setLastAppliedMapDataUpdatedAt] = useState(0);
  const [hasLoadedViewportMapResults, setHasLoadedViewportMapResults] = useState(false);

  const hasLoadedMissions = useMemo(() => lastAppliedMapDataUpdatedAt !== 0, [lastAppliedMapDataUpdatedAt]);

  const selectedFilterIdsKey = useMemo(() => {
    return (selectedFilterItems ?? [])
      .map((x) => `${x.id}`)
      .sort()
      .join('|');
  }, [selectedFilterItems]);

  const hasFiniteCoordinates = (location?: MapLocation) => Number.isFinite(location?.lat) && Number.isFinite(location?.lng);

  const regularMissionCenter = effectiveLocation ?? serviceLocation ?? userLocation ?? currentLocation ?? fallBackLocation;
  const airportSearchCenter = userLocation ?? effectiveLocation ?? serviceLocation ?? fallBackLocation;
  const viewportTopLeft = useMemo(() => parseBoundsCoordinate(appliedTopLeftLocation), [appliedTopLeftLocation]);
  const viewportBottomRight = useMemo(() => parseBoundsCoordinate(appliedBottomRightLocation), [appliedBottomRightLocation]);
  const airportViewportCenter = useMemo(() => {
    if (!viewportTopLeft || !viewportBottomRight) {
      return undefined;
    }

    return {
      lat: (viewportTopLeft.lat + viewportBottomRight.lat) / 2,
      lng: (viewportTopLeft.lng + viewportBottomRight.lng) / 2,
    };
  }, [viewportTopLeft, viewportBottomRight]);
  const shouldUseAirportViewportBounds = hasViewportBounds && mapMode !== MapMode.INITIAL_LOAD && !!viewportTopLeft && !!viewportBottomRight;
  const airportQueryLocation = shouldUseAirportViewportBounds ? airportViewportCenter : airportSearchCenter;
  const selectedBranchDetailLocation = currentLocation ?? userLocation ?? serviceLocation ?? fallBackLocation;
  const hasRegularMissionCenter = hasFiniteCoordinates(regularMissionCenter);
  const hasAirportSearchCenter = hasFiniteCoordinates(airportSearchCenter);
  const hasAirportQueryLocation = hasFiniteCoordinates(airportQueryLocation);

  const hasResolvedOrFallbackPosition = hasResolvedUserPosition || hasRegularMissionCenter;

  const canFetchRegularMapMissions =
    locationReady &&
    hasResolvedOrFallbackPosition &&
    hasRegularMissionCenter &&
    !apiMissions.isCancelled &&
    !waitingForBrowserLocation &&
    (mapMode !== MapMode.INITIAL_LOAD || !hasGpsFix || hasCenteredOnEffectiveLocation);

  const canFetchAirportMissions = hasAirportQueryLocation && !apiAirportMissions.isCancelled;
  const canFetchAirportListCards = hasAirportSearchCenter && !apiMissions.isCancelled;

  const canFetchMapMissions = canFetchRegularMapMissions;

  useEffect(() => {
    if (!canFetchRegularMapMissions) {
      setLoadingSideContent(false);
    }
  }, [canFetchRegularMapMissions]);

  useEffect(() => {
    setHasLoadedViewportMapResults(false);
  }, [missionQuerySeed]);

  const mapQuery = useQuery({
    enabled: canFetchRegularMapMissions,
    useErrorBoundary: false,
    staleTime: 30_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: [
      'customMap',
      'missions',
      regularMissionCenter?.lat,
      regularMissionCenter?.lng,
      selectedFilterIdsKey,
      missionQuerySeed,
      uiLanguage,
      appliedTopLeftLocation,
      appliedBottomRightLocation,
      onlyAcceptableMissions,
      showUpcomingMissions,
    ],
    queryFn: ({ signal }) => {
      const requestToken = axios.CancelToken.source();
      signal?.addEventListener('abort', () => {
        requestToken.cancel('getMissionsForMap: Query was cancelled by React Query');
      });

      if (lastAppliedMapDataUpdatedAt === 0) {
        setLoadingSideContent(true);
      }

      const sendViewportBounds = hasViewportBounds && mapMode !== MapMode.INITIAL_LOAD;
      return apiMissions.getMissionsFromMapv2(
        selectedFilterItems.map((x) => x.id),
        `${regularMissionCenter!.lat}&${regularMissionCenter!.lng}`,
        sendViewportBounds ? appliedBottomRightLocation : undefined,
        sendViewportBounds ? appliedTopLeftLocation : undefined,
        onlyAcceptableMissions,
        showUpcomingMissions,
        requestToken.token,
      );
    },
    onError: () => {
      setMissionCardProps([]);
      setPinnedMissionCardProps([]);
      setFlashMissionCardProps([]);
      setMissions([]);
      setLoadingSideContent(false);
      setLastAppliedMapDataUpdatedAt(0);
      setHasLoadedViewportMapResults(false);
    },
    onSettled: () => {
      setLoadingSideContent(false);
    },
  });

  const airportMapQuery = useQuery({
    enabled: canFetchAirportMissions,
    useErrorBoundary: false,
    staleTime: 30_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: [
      'customMap',
      'airportMissions',
      airportQueryLocation?.lat,
      airportQueryLocation?.lng,
      selectedFilterIdsKey,
      missionQuerySeed,
      uiLanguage,
      shouldUseAirportViewportBounds ? appliedTopLeftLocation : undefined,
      shouldUseAirportViewportBounds ? appliedBottomRightLocation : undefined,
      onlyAcceptableMissions,
      showUpcomingMissions,
    ],
    queryFn: async ({ signal }) => {
      const requestToken = axios.CancelToken.source();
      signal?.addEventListener('abort', () => {
        requestToken.cancel('getAirportMissionsForMap: Query was cancelled by React Query');
      });

      const selectedTags = selectedFilterItems.map((x) => x.id);
      const location = `${airportQueryLocation!.lat}&${airportQueryLocation!.lng}`;

      const requestAirportMissions = (bottomRightLocation?: string, topLeftLocation?: string) =>
        apiAirportMissions.getAirportMissionsFromMapV2(
          selectedTags,
          bottomRightLocation,
          topLeftLocation,
          location,
          onlyAcceptableMissions,
          showUpcomingMissions,
          requestToken.token,
        );

      try {
        return await requestAirportMissions(
          shouldUseAirportViewportBounds ? appliedBottomRightLocation : undefined,
          shouldUseAirportViewportBounds ? appliedTopLeftLocation : undefined,
        );
      } catch (error) {
        if (axios.isCancel(error) || signal?.aborted) {
          throw error;
        }

        const wideBounds = buildWideAirportBounds(airportQueryLocation!.lat, airportQueryLocation!.lng);
        return await requestAirportMissions(wideBounds.bottomRight, wideBounds.topLeft);
      }
    },
  });

  useQuery({
    enabled: canFetchAirportListCards,
    useErrorBoundary: false,
    staleTime: 300_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: ['customMap', 'airportMissions', 'listCards', airportSearchCenter?.lat, airportSearchCenter?.lng, missionQuerySeed, uiLanguage],
    queryFn: ({ signal }) => {
      const requestToken = axios.CancelToken.source();
      signal?.addEventListener('abort', () => {
        requestToken.cancel('getAirportMissionsForList: Query was cancelled by React Query');
      });

      const location = `${airportSearchCenter!.lat}&${airportSearchCenter!.lng}`;
      return apiMissions.getAirportMissionsFromMapV2(
        undefined,
        GLOBAL_AIRPORT_BOTTOM_RIGHT_BOUNDS,
        GLOBAL_AIRPORT_TOP_LEFT_BOUNDS,
        location,
        false,
        true,
        requestToken.token,
      );
    },
    onSuccess: (data) => {
      const airportCards = mapAirportGroupsToCards(data?.airports ?? []);
      setInitialAirportMissionCardProps(airportCards);
      setAirportMissionCardProps(airportCards);
    },
  });

  const shouldShowMapMissionLoading = loadingSideContent || (canFetchRegularMapMissions && mapQuery.isFetching);

  const selectedBranchMissionDetailQuery = useQuery({
    enabled:
      sideBarState === SideBarState.Details &&
      !!branchMarker &&
      !!selectedBranchDetailLocation &&
      !loadingSideContent &&
      !!missions &&
      missions.findIndex((mission) => mission.branchId === branchMarker) === -1 &&
      selectedMissionCardForDetailsId !== branchMarker,
    useErrorBoundary: false,
    queryKey: ['customMap', 'branchMissionDetails', branchMarker, selectedBranchDetailLocation?.lat, selectedBranchDetailLocation?.lng, uiLanguage],
    queryFn: () => {
      return apiMissions.getMissionDetails(
        branchMarker,
        `${selectedBranchDetailLocation!.lat}&${selectedBranchDetailLocation!.lng}`,
        false,
        undefined,
      );
    },
  });

  const selectedMissionFallback = useMemo(() => {
    if (!branchMarker || !selectedBranchMissionDetailQuery.data?.missionDetails?.length) {
      return undefined;
    }

    const firstMissionDetail = selectedBranchMissionDetailQuery.data.missionDetails[0];
    const missionFallback = new GetMissionsFromMapDto();

    missionFallback.branchId = branchMarker;
    missionFallback.name = firstMissionDetail.name;
    missionFallback.phone = firstMissionDetail.phone;
    missionFallback.hasLogo = firstMissionDetail.hasLogo;
    missionFallback.logoUrl = firstMissionDetail.logoUrl;
    missionFallback.address = firstMissionDetail.address;
    missionFallback.distance = firstMissionDetail.distance;
    missionFallback.tags = firstMissionDetail.tags;
    missionFallback.canAccept = true;
    missionFallback.geocoordinate = firstMissionDetail.geocoordinate;
    missionFallback.missionType = resolveMissionType(firstMissionDetail);
    missionFallback.branchAirportInfo = (
      firstMissionDetail as {
        branchAirportInfo?: GetMissionsFromMapDto['branchAirportInfo'];
      }
    ).branchAirportInfo;
    missionFallback.isFlashMission = resolveIsFlashMission(firstMissionDetail);
    missionFallback.missionConfigurationNames = selectedBranchMissionDetailQuery.data.missionDetails
      .map((detail) => detail.missionConfigurationName)
      .filter((name): name is string => !!name);
    missionFallback.isAvailableForDiscovery = true;

    return missionFallback;
  }, [branchMarker, selectedBranchMissionDetailQuery.data?.missionDetails]);

  useEffect(() => {
    if (mapQuery.isSuccess) {
      const result = mapQuery.data;

      if (!result) {
        setLastAppliedMapDataUpdatedAt(mapQuery.dataUpdatedAt);
        return;
      }

      const highOrderMissions = result.highOrderMissions ?? [];
      const regularOrderMissions = result.missions ?? [];
      const lowOrderMissions = result.lowOrderMissions ?? [];
      const allMissions = [...highOrderMissions, ...regularOrderMissions, ...lowOrderMissions];

      const hasCoordinates = (m: GetMissionsFromMapDto) => m.geocoordinate?.x !== undefined && m.geocoordinate?.y !== undefined;

      const pinnedMissionsList = highOrderMissions
        .filter((m) => hasCoordinates(m) && resolveMissionType(m) === MissionType.Regular && !resolveIsFlashMission(m))
        .map((m): MissionCardProps => MissionsHelper.mapToMissionCard(m, () => undefined));

      const missionsList = [...regularOrderMissions, ...lowOrderMissions]
        .filter((m) => hasCoordinates(m) && resolveMissionType(m) === MissionType.Regular && !resolveIsFlashMission(m))
        .map((m): MissionCardProps => MissionsHelper.mapToMissionCard(m, () => undefined));

      const flashMissionsList = allMissions
        .filter((m) => hasCoordinates(m) && resolveMissionType(m) === MissionType.Regular && resolveIsFlashMission(m))
        .map((m): MissionCardProps => MissionsHelper.mapToMissionCard(m, () => undefined));

      const selectedTagIds = new Set((selectedFilterItems ?? []).map((x) => `${x.id}`));
      const nextFilterItems = (result.tags ?? []).map(
        (s) =>
          ({
            id: `${s.id}`,
            name: s.name === '' ? 'N/D' : s.name,
            highlight: false,
            checked: selectedTagIds.has(`${s.id}`),
          }) as FilterItemProps,
      );

      const nextFilterKey = nextFilterItems.map((x) => `${x.id}:${x.checked ? 1 : 0}`).join('|');
      const currentFilterKey = (filterItems ?? []).map((x) => `${x.id}:${x.checked ? 1 : 0}`).join('|');
      if (nextFilterKey !== currentFilterKey) {
        setFilterItems(nextFilterItems);
      }

      const nextSelected = nextFilterItems.filter((x) => x.checked);
      const nextSelectedKey = nextSelected
        .map((x) => `${x.id}`)
        .sort()
        .join('|');
      if (
        nextSelectedKey !==
        (selectedFilterItems ?? [])
          .map((x) => `${x.id}`)
          .sort()
          .join('|')
      ) {
        setSelectedFilterItems(nextSelected);
      }

      setInitialMissionCardProps(missionsList);
      setInitialPinnedMissionCardProps(pinnedMissionsList);
      setInitialFlashMissionCardProps(flashMissionsList);
      setMissionCardProps(missionsList);
      setPinnedMissionCardProps(pinnedMissionsList);
      setFlashMissionCardProps(flashMissionsList);
      setMissions(allMissions);
      setLastAppliedMapDataUpdatedAt(mapQuery.dataUpdatedAt);
      if (hasViewportBounds && mapMode !== MapMode.INITIAL_LOAD && appliedTopLeftLocation && appliedBottomRightLocation) {
        setHasLoadedViewportMapResults(true);
      }
      setLoadingSideContent(false);
    }
  }, [mapQuery.isSuccess, mapQuery.data, mapQuery.dataUpdatedAt, filterItems, selectedFilterItems]);

  useEffect(() => {
    if (!airportMapQuery.isSuccess) {
      return;
    }

    const airportGroups = airportMapQuery.data?.airports ?? [];

    setAirportMapGroups(airportGroups);
  }, [airportMapQuery.isSuccess, airportMapQuery.data]);

  useEffect(() => {
    if ((airportMissionCardProps?.length ?? 0) > 0 || (airportMapGroups?.length ?? 0) <= 0) {
      return;
    }

    const fallbackAirportCards = mapAirportGroupsToCards(airportMapGroups);
    if (fallbackAirportCards.length <= 0) {
      return;
    }

    setInitialAirportMissionCardProps((prev) => (prev.length > 0 ? prev : fallbackAirportCards));
    setAirportMissionCardProps(fallbackAirportCards);
  }, [airportMapGroups, airportMissionCardProps]);

  return {
    missions,
    airportMapGroups,
    missionCardProps,
    setMissionCardProps,
    airportMissionCardProps,
    setAirportMissionCardProps,
    airportMapGroupsState: airportMapGroups,
    setAirportMapGroups,
    pinnedMissionCardProps,
    setPinnedMissionCardProps,
    flashMissionCardProps,
    setFlashMissionCardProps,
    initialMissionCardProps,
    initialPinnedMissionCardProps,
    initialFlashMissionCardProps,
    initialAirportMissionCardProps,
    filterItems,
    setFilterItems,
    selectedFilterItems,
    setSelectedFilterItems,
    onlyAcceptableMissions,
    setOnlyAcceptableMissions,
    showUpcomingMissions,
    setShowUpcomingMissions,
    canFetchMapMissions,
    canFetchAirportMissions,
    loadingSideContent,
    shouldShowMapMissionLoading,
    hasLoadedMissions,
    hasLoadedViewportMapResults,
    mapQuery,
    airportMapQuery,
    selectedBranchMissionDetailQuery,
    selectedMissionFallback,
  };
};

export default useMissionsData;
